import { Component, createRef } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { DropboxFile } from '../../../models/dropbox-file';
import { GoogleDriveFile } from '../../../models/google-drive-file';
import { Logger } from '../../../models/logger';
import { GAService } from '../../../services/analytics.service';
import { DropboxService } from '../../../services/dropbox.service';
import { GoogleDriverService } from '../../../services/google-driver.service';
import { MotivesService } from '../../../services/motives.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { FileSelectEvent, FileUploadProps } from '../../../types/file-upload.types';
import { GAContants } from '../../../types/ga.types';
import { IconHelper } from '../../../utils/icon-helper';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import { MotiveLocationTypes } from '../../../utils/request.utils';
import Modal from '../modal/modal.component';
import { MotiveComponent } from '../motive/motive.component';
import { AdSlots } from '../../ui/motive/ad-slots';
import styles from './file-upload.modal.module.scss';



interface FileUploadModalState {
    isDragging: boolean;
}

export default class FileUploadModal extends Component<FileUploadProps, FileUploadModalState> {
    private readonly logger = Logger.getLogger('FileUploadModal');

    static show(props: any) {
        Modal.createModal(FileUploadModal, props);
    }

    @lazyInject(GAService) private gaService: GAService;
    @lazyInject(GoogleDriverService) private driver: GoogleDriverService;
    @lazyInject(DropboxService) private dropbox: DropboxService;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;
    @lazyInject(SpinnerService) private spinner: SpinnerService;
    @lazyInject(MotivesService) public motivesService: MotivesService;
    @lazyInject(UIContext) private uiContext: UIContext;

    modalRef = createRef<Modal>();

    _fileSelectButtonClicked = false;
    dragCounter = 0;
    readonly state: Readonly<FileUploadModalState> = { isDragging: false };

    dialogShown = false;
    selectedAtleastOneFile = false;

    constructor(props) {
        super(props);
    }

    async fileUploadBtnClicked(): Promise<void> {
        if (!await this.showProductMotive()) {
            return;
        }
        this.createAndClickFileUploadElement();
        // this.imgFileInput.nativeElement.click();
        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory.BUTTON,
            GAContants.EventAction['FILE-SELECT-DIALOG-OPENED'],
            GAContants.EventLabel.UPLOAD,
            '',
        );
    }

    private createAndClickFileUploadElement() {
        const input: HTMLInputElement = document.createElement('input');
        input.multiple = !this.props.singleFileSelect;
        input.type = 'file';
        input.hidden = true;
        input.accept = this.props.acceptedFileTypes;
        input.onchange = ($event) => this.processFileSelectedEvent($event);
        document.querySelector(this.props.fileUploadInputContainerCSSSelector).appendChild(input);
        input.click();
    }

    async processFileSelectedEvent(event: Event | DragEvent): Promise<void> {
        event.stopPropagation();
        event.preventDefault();
        this.setState({ isDragging: false });
        try {
            await this.initializeScriptsAndWebsockets();
        } catch (e) {
            this.logger.error({ message: 'Something went wrong while loading scripts', error: e });
        }
        const files: File[] = event['target']['files'] || event['dataTransfer'].files;
        this.fileSelected({
            files: files,
        });
        // this.close();
        this.selectedAtleastOneFile = true;
    }

    handleDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter++;
        if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
            this.setState({ isDragging: true });
        }
    };

    handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter--;
        if (this.dragCounter === 0) {
            this.setState({ isDragging: false });
        }
    };

    handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();
    };

    handleDrop = async (event: React.DragEvent<HTMLDivElement>): Promise<void> => {
        this.dragCounter = 0;
        await this.processFileSelectedEvent(event.nativeEvent);
    };

    async openFileDialog() {
        if (!await this.showProductMotive()) {
            return;
        }
        await this.initializeScriptsAndWebsockets();
        await Promise.all([this.scriptLoadService.loadGoogleApi(), this.scriptLoadService.loadGoogleIdentityServices()]);
        this.driver.showFileSelectDialog((files: GoogleDriveFile[]) => {
            this.fileSelected({ files: files });
            // this.close();
        });
        this.selectedAtleastOneFile = true;
    }

    async openDropboxFileDialog() {
        if (!await this.showProductMotive()) {
            return;
        }
        await this.initializeScriptsAndWebsockets();
        this.dropbox.showFileSelectDialog((files: DropboxFile[]) => {
            this.fileSelected({ files: files });
            // this.close();
        });
        this.selectedAtleastOneFile = true;
    }

    private fileSelected(event: FileSelectEvent) {
        this.props.fileSelected(event);
        this.modalRef.current.unmount();
    }

    async initializeScriptsAndWebsockets() {
        this._fileSelectButtonClicked = true;
        this.props.fileSelectButtonClicked();
        await this.spinner.runWithLoader(async () => {
            try {
                await this.scriptLoadService.loadAllScriptsIfNotLoaded();
            } catch (e) {
                this.logger.error({ message: 'Something went wrong while loading scripts.', error: e });
                this.uiContext.showErrorDialog(
                    {
                        title: 'Failed to initialize services!', text: 'Following services failed: ' +
                            this.scriptLoadService.getFailedScripts().join(', ') +
                            '. Please send us a screenshot to (support@xconvert.com).' +
                            ' Please try loading the page on Incognito Mode with plugins disabled.'
                    },
                );
            }

        }, 'Please wait while we initialize file upload service...');
        // this.show();
    }


    async showProductMotive() {
        // if (this.loginService.motiveSettings.show7DayTrialDialog) {
        //     const result = await this.uiContext.showHTMLDialog();
        //     if (result && result.value) {
        //         this.router.navigate(["product"]);
        //         return false;
        //     }
        // }
        return true;
    }

    render() {
        return <Modal ref={this.modalRef}>
            <div data-modalheader>
                <h2>Select Upload Method</h2>
            </div>
            <div data-modalbody>
                <div className={styles["options-container"]}>
                    <div
                        className={`${styles["drop-zone"]} ${this.state.isDragging ? styles["drop-zone-active"] : ""}`}
                        onDragEnter={this.handleDragEnter}
                        onDragLeave={this.handleDragLeave}
                        onDragOver={this.handleDragOver}
                        onDrop={this.handleDrop}
                    >
                        <div className={styles["drop-zone-content"]}>
                            {IconHelper.getIconElement(HtmlEntityTypes.DESKTOP_COMPUTER)}
                            <p className={styles["drop-zone-text"]}>
                                {this.state.isDragging
                                    ? "Drop files here..."
                                    : "Drag & Drop files here"}
                            </p>
                            <span className={styles["drop-zone-or"]}>or</span>
                        </div>
                    </div>

                    <div className={`gcontent-container ${styles["gcontent-container mb-3"]}`}>
                        <MotiveComponent
                            data={AdSlots.inContent} containerWidth="100%" containerHeight="90px"
                            adWidth="970px" adHeight="90px" showAds={true} location={MotiveLocationTypes.fileUploadPageTop}
                            className='width_major'
                        ></MotiveComponent>
                    </div>
                    <div className={styles["option"]} onClick={() => this.fileUploadBtnClicked()}>
                        <div>
                            <div>{
                                IconHelper.getIconElement(HtmlEntityTypes.DESKTOP_COMPUTER)
                            } Select From Local Disk
                            </div>
                        </div>
                    </div>
                    <div className={styles["option"]} onClick={() => this.openDropboxFileDialog()}>
                        <div>
                            <div>
                                <img className={styles["google-drive-image"]} src="/img/dropbox.png" />
                                <span>Select From Dropbox (Fast)</span>
                            </div>
                        </div>
                    </div>
                    <div className={styles["option"]} onClick={() => this.openFileDialog()}>
                        <div>
                            <div>
                                <img className={styles["google-drive-image"]} src="/img/google-drive.png" />
                                <span>Select From Google Drive (Fast)</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ margin: '1rem' }}>
                        <div className="alert alert-danger">
                            <b>{
                                IconHelper.getIconElement(HtmlEntityTypes.WARNING)
                            } If you experience slow upload or download speeds, please using Google Drive or Dropbox to upload files.
                                Use Dropbox to download files. Why use Google Drive or Dropbox?</b> This might seems an inconvenience.
                            But Google and Dropbox have a large network of servers all over the world.
                            It is incredibly fast. It can reduce your file upload time.
                            Our servers are extremely fast at processing files.
                            But we are not in control of the speed of your connection to our servers.
                        </div>
                    </div>
                    <div className={`gcontent-container ${styles["gcontent-container mt-3"]}`}>
                        <MotiveComponent
                            data={AdSlots.inContent} containerWidth="100%" containerHeight="90px"
                            adWidth="970px" adHeight="90px" showAds={true} location={MotiveLocationTypes.fileUploadPageBottom}
                            className='width_major'
                        ></MotiveComponent>
                    </div>
                </div>
            </div>
        </Modal >
    }
}
